import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, TextField, InputAdornment, IconButton, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import emitter from 'tiny-emitter/instance';

import FilterButton from './FilterButton';
import SetFilterPopover from './SetFilterPopover';
import apiData from '../services/api_data';

const keyOfOption = (isObjectLike) => (v) => (isObjectLike && v && typeof v === 'object' ? v.itemName : v);

// Visible options for a dimension. Cascades against the live facets response (post pending-filter)
// so picking "TV" narrows other dimensions to combinations that exist. Always includes currently
// selected names so a user can deselect even if the cascade no longer lists their pick.
function computeVisible(cachedUnion, facetsList, selectedNames, isObjectLike) {
	const keyOf = keyOfOption(isObjectLike);
	const baseList = Array.isArray(facetsList) ? facetsList : (Array.isArray(cachedUnion) ? cachedUnion : []);
	const seen = new Set();
	const result = [];

	baseList.forEach((item) => {
		const k = keyOf(item);
		if (k != null && !seen.has(k)) {
			seen.add(k);
			result.push(item);
		}
	});

	(selectedNames || []).forEach((name) => {
		if (typeof name !== 'string' || seen.has(name)) return;
		seen.add(name);
		const cached = (cachedUnion || []).find((o) => keyOf(o) === name);
		result.push(cached ?? (isObjectLike ? { id: name, itemName: name } : name));
	});

	return result;
}

// sessionStorage filter values may be names (current format) or legacy indices. Drop indices —
// stale indices into a since-shrunk list would point to wrong items, so it's safer to discard.
function initialNames(key) {
	const stored = JSON.parse(sessionStorage.getItem('filters'))?.[key];
	return Array.isArray(stored) ? stored.filter((v) => typeof v === 'string') : [];
}

export default function FormFilters(props) {
	const classes = useStyles();

	const [search, setSearch] = useState(JSON.parse(sessionStorage.getItem('filters'))?.search || '');
	const lastEmittedSearchRef = useRef(search);

	// Cached unions per dimension — append-only across refetches so options don't disappear when
	// props.data shrinks to the filtered subset, and so currently-selected names can always be
	// rendered as full option objects even after the cascade narrows them away.
	const [mediaType, setMediaType] = useState([]);
	const [selectedMediaType, setSelectedMediaType] = useState(() => initialNames('mediaType'));
	const [brand, setBrand] = useState([]);
	const [selectedBrand, setSelectedBrand] = useState(() => initialNames('brand'));
	const [mediaSource, setMediaSource] = useState([]);
	const [selectedMediaSource, setSelectedMediaSource] = useState(() => initialNames('mediaSource'));
	const [category, setCategory] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState(() => initialNames('category'));
	const [subCategory, setSubCategory] = useState([]);
	const [selectedSubCategory, setSelectedSubCategory] = useState(() => initialNames('subCategory'));
	const [type, setType] = useState([]);
	const [selectedType, setSelectedType] = useState(() => initialNames('type'));
	const [sentiment, setSentiment] = useState([]);
	const [selectedSentiment, setSelectedSentiment] = useState(() => initialNames('sentiment'));
	const [author, setAuthor] = useState([]);
	const [selectedAuthor, setSelectedAuthor] = useState(() => initialNames('author'));
	const [topic, setTopic] = useState([]);
	const [selectedTopic, setSelectedTopic] = useState(() => initialNames('topic'));
	const [advertType, setAdvertType] = useState([]);
	const [selectedAdvertType, setSelectedAdvertType] = useState(() => initialNames('advertType'));
	const [language, setLanguage] = useState([]);
	const [selectedLanguage, setSelectedLanguage] = useState(() => initialNames('language'));
	const [country, setCountry] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState(() => initialNames('country'));
	const [location, setLocation] = useState([]);
	const [selectedLocation, setSelectedLocation] = useState(() => initialNames('location'));

	// Live cascade response. Null means "no cascade applied yet" — dimensions fall back to cached unions.
	const [facets, setFacets] = useState(null);
	const cascadeReqId = useRef(0);

	// Active popover state. activeDim is the dimension key from DIMENSIONS below, or null when closed.
	const [activeDim, setActiveDim] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

	const optionsRef = useRef({
		mediaType: [], brand: [], mediaSource: [], category: [], subCategory: [],
		type: [], sentiment: [], author: [], topic: [], advertType: [],
		language: [], country: [], location: [],
	});

	// Tracks which client the accumulated options in optionsRef belong to. mergeOptions
	// only ever adds options (so a selected value stays visible as the cascade narrows
	// props.data); without this, switching clients leaves the previous client's options
	// merged in. On a client change we clear optionsRef so the dropdowns reflect the new
	// client only.
	const optionsClientRef = useRef(null);

	const mergeOptions = (existing, incoming) => {
		if (!Array.isArray(incoming) || incoming.length === 0) return existing;
		const isObjectLike = incoming.some((v) => v && typeof v === 'object');
		const keyOf = isObjectLike ? (v) => (v && typeof v === 'object' ? v.itemName ?? v.id : v) : (v) => v;
		const seen = new Set(existing.map(keyOf));
		const out = existing.slice();
		incoming.forEach((item) => {
			const key = keyOf(item);
			if (key != null && !seen.has(key)) {
				seen.add(key);
				out.push(item);
			}
		});
		return out;
	};

	useEffect(() => {
		if (!props.data) return;
		const r = optionsRef.current;

		// Reset accumulated options when the client whose data we're showing changes.
		// We key on dataFilter.clientId (written in Router right before getAnalytics)
		// rather than selectedClient.id: dataFilter.clientId flips exactly when the new
		// client's analytics response lands, whereas selectedClient changes one render
		// earlier — while props.data still holds the previous client's filter arrays.
		const currentClientId =
			JSON.parse(sessionStorage.getItem('dataFilter') || 'null')?.clientId ?? null;
		if (optionsClientRef.current !== currentClientId) {
			optionsClientRef.current = currentClientId;
			Object.keys(r).forEach((k) => {
				r[k] = [];
			});
			// Drop the previous client's cascade facets too — computeVisible prefers
			// facetsList over the cached union, so stale facets would keep surfacing the
			// old client's options until the user changes a selection.
			setFacets(null);
		}

		r.mediaType = mergeOptions(r.mediaType, props.data.mediaTypes);
		r.brand = mergeOptions(r.brand, props.data.brands);
		r.mediaSource = mergeOptions(r.mediaSource, props.data.mediaSources);
		r.category = mergeOptions(r.category, props.data.categories);
		r.subCategory = mergeOptions(r.subCategory, props.data.subcategories);
		r.type = mergeOptions(r.type, props.data.types);
		r.sentiment = mergeOptions(r.sentiment, props.data.sentiment);
		r.author = mergeOptions(r.author, props.data.authors);
		r.topic = mergeOptions(r.topic, props.data.topics);
		r.advertType = mergeOptions(r.advertType, props.data.advertTypes);
		r.language = mergeOptions(r.language, props.data.languages);
		r.country = mergeOptions(r.country, props.data.countries);
		r.location = mergeOptions(r.location, props.data.locations);

		setMediaType(r.mediaType);
		setBrand(r.brand);
		setMediaSource(r.mediaSource);
		setCategory(r.category);
		setSubCategory(r.subCategory);
		setType(r.type);
		setSentiment(r.sentiment);
		setAuthor(r.author);
		setTopic(r.topic);
		setAdvertType(r.advertType);
		setLanguage(r.language);
		setCountry(r.country);
		setLocation(r.location);
	}, [props.data]);

	// Cascade fetch: each time committed selections change, debounce-fire a facets call so OTHER
	// dimensions narrow to combinations that still exist. The active dimension's options stay
	// broad via computeVisible's selected-always-visible rule (user can still deselect).
	useEffect(() => {
		const handle = setTimeout(() => {
			const base = JSON.parse(sessionStorage.getItem('dataFilter') || 'null');
			if (!base) return;
			const pending = {
				...base,
				mediaType: selectedMediaType,
				brand: selectedBrand,
				mediaSource: selectedMediaSource,
				category: selectedCategory,
				subCategory: selectedSubCategory,
				type: selectedType,
				sentiment: selectedSentiment,
				author: selectedAuthor,
				topic: selectedTopic,
				advertType: selectedAdvertType,
				language: selectedLanguage,
				country: selectedCountry,
				location: selectedLocation,
				exclude: [],
				search: typeof search === 'string' ? search : '',
			};
			const myId = ++cascadeReqId.current;
			apiData.getFilterFacets(pending)
				.then((response) => {
					if (myId !== cascadeReqId.current) return;
					setFacets(response || null);
				})
				.catch(() => { /* leave previous facets; dimensions fall back to cached union */ });
		}, 300);
		return () => clearTimeout(handle);
	}, [
		selectedMediaType, selectedBrand, selectedMediaSource, selectedCategory, selectedSubCategory,
		selectedType, selectedSentiment, selectedAuthor, selectedTopic, selectedAdvertType,
		selectedLanguage, selectedCountry, selectedLocation, search,
	]);

	const facetSources = useMemo(() => (facets?.sources || []).map((s) => ({ id: s, itemName: s })), [facets]);
	const facetSentiment = useMemo(() => (facets?.sentiments || []).map((s) => ({ id: s, itemName: s })), [facets]);
	const facetAuthors = useMemo(() => (facets?.authors || []).map((a) => ({ id: a, itemName: a })), [facets]);
	const facetAdvertTypes = useMemo(() => (facets?.advertisingtypes || []).map((a) => ({ id: a, itemName: a })), [facets]);
	const facetLanguages = useMemo(() => (facets?.languages || []).map((l) => ({ id: l, itemName: l })), [facets]);
	const facetLocations = useMemo(() => (facets?.regions || []).map((r) => ({ id: r, itemName: r })), [facets]);

	const visibleMediaType = useMemo(() => computeVisible(mediaType, facets?.mediatypes, selectedMediaType, false), [mediaType, facets, selectedMediaType]);
	const visibleBrand = useMemo(() => computeVisible(brand, facets?.brands, selectedBrand, false), [brand, facets, selectedBrand]);
	const visibleMediaSource = useMemo(() => computeVisible(mediaSource, facetSources, selectedMediaSource, true), [mediaSource, facetSources, selectedMediaSource]);
	const visibleCategory = useMemo(() => computeVisible(category, facets?.categories, selectedCategory, false), [category, facets, selectedCategory]);
	const visibleSubCategory = useMemo(() => computeVisible(subCategory, facets?.subcategories, selectedSubCategory, false), [subCategory, facets, selectedSubCategory]);
	const visibleType = useMemo(() => computeVisible(type, facets?.types, selectedType, false), [type, facets, selectedType]);
	const visibleSentiment = useMemo(() => computeVisible(sentiment, facetSentiment, selectedSentiment, true), [sentiment, facetSentiment, selectedSentiment]);
	const visibleAuthor = useMemo(() => computeVisible(author, facetAuthors, selectedAuthor, true), [author, facetAuthors, selectedAuthor]);
	const visibleTopic = useMemo(() => computeVisible(topic, facets?.topics, selectedTopic, false), [topic, facets, selectedTopic]);
	const visibleAdvertType = useMemo(() => computeVisible(advertType, facetAdvertTypes, selectedAdvertType, true), [advertType, facetAdvertTypes, selectedAdvertType]);
	const visibleLanguage = useMemo(() => computeVisible(language, facetLanguages, selectedLanguage, true), [language, facetLanguages, selectedLanguage]);
	const visibleCountry = useMemo(() => computeVisible(country, facets?.countries, selectedCountry, false), [country, facets, selectedCountry]);
	const visibleLocation = useMemo(() => computeVisible(location, facetLocations, selectedLocation, true), [location, facetLocations, selectedLocation]);

	// Registry per dimension. Keys mirror the sessionStorage filter shape so persist/emit can
	// loop over a single source of truth instead of repeating the 13 fields 4x.
	const DIMENSIONS = [
		{ key: 'mediaType', label: 'Media Type', selected: selectedMediaType, setSelected: setSelectedMediaType, options: visibleMediaType, emitField: 'mediaType' },
		{ key: 'brand', label: 'Brand', selected: selectedBrand, setSelected: setSelectedBrand, options: visibleBrand, emitField: 'brand' },
		{ key: 'mediaSource', label: 'Media Source', selected: selectedMediaSource, setSelected: setSelectedMediaSource, options: visibleMediaSource, emitField: 'mediaSource' },
		{ key: 'category', label: 'Category', selected: selectedCategory, setSelected: setSelectedCategory, options: visibleCategory, emitField: 'category' },
		{ key: 'subCategory', label: 'Sub Category', selected: selectedSubCategory, setSelected: setSelectedSubCategory, options: visibleSubCategory, emitField: 'subCategory' },
		{ key: 'type', label: 'Type', selected: selectedType, setSelected: setSelectedType, options: visibleType, emitField: 'type' },
		{ key: 'sentiment', label: 'Sentiment', selected: selectedSentiment, setSelected: setSelectedSentiment, options: visibleSentiment, emitField: 'sentiment' },
		{ key: 'author', label: 'Author', selected: selectedAuthor, setSelected: setSelectedAuthor, options: visibleAuthor, emitField: 'author' },
		{ key: 'topic', label: 'Topic', selected: selectedTopic, setSelected: setSelectedTopic, options: visibleTopic, emitField: 'topic' },
		{ key: 'advertType', label: 'Advert Type', selected: selectedAdvertType, setSelected: setSelectedAdvertType, options: visibleAdvertType, emitField: 'advertType' },
		{ key: 'language', label: 'Language', selected: selectedLanguage, setSelected: setSelectedLanguage, options: visibleLanguage, emitField: 'language' },
		{ key: 'country', label: 'Country', selected: selectedCountry, setSelected: setSelectedCountry, options: visibleCountry, emitField: 'country' },
		{ key: 'location', label: 'Location', selected: selectedLocation, setSelected: setSelectedLocation, options: visibleLocation, emitField: 'region' },
	];

	const dimByKey = (k) => DIMENSIONS.find((d) => d.key === k);

	// Build the complete filter payload using a single override so the in-flight dimension's value
	// is included immediately — React state setter is async, can't be read back synchronously.
	function persistAndEmit(overrideKey, overrideValue, overrideSearch) {
		const valueFor = (key) => (key === overrideKey ? overrideValue : dimByKey(key).selected);
		const searchValue = overrideSearch !== undefined ? overrideSearch : (typeof search === 'string' ? search : '');

		const filters = {
			mediaType: valueFor('mediaType'),
			brand: valueFor('brand'),
			mediaSource: valueFor('mediaSource'),
			category: valueFor('category'),
			subCategory: valueFor('subCategory'),
			type: valueFor('type'),
			sentiment: valueFor('sentiment'),
			author: valueFor('author'),
			topic: valueFor('topic'),
			advertType: valueFor('advertType'),
			language: valueFor('language'),
			country: valueFor('country'),
			location: valueFor('location'),
			exclude: [],
			search: searchValue,
		};

		sessionStorage.setItem('filters', JSON.stringify(filters));

		if (!props.data) return;

		emitter.emit('filter', [
			{ field: 'mediaType', values: filters.mediaType },
			{ field: 'brand', values: filters.brand },
			{ field: 'mediaSource', values: filters.mediaSource },
			{ field: 'category', values: filters.category },
			{ field: 'subCategory', values: filters.subCategory },
			{ field: 'type', values: filters.type },
			{ field: 'sentiment', values: filters.sentiment },
			{ field: 'author', values: filters.author },
			{ field: 'topic', values: filters.topic },
			{ field: 'advertType', values: filters.advertType },
			{ field: 'language', values: filters.language },
			{ field: 'country', values: filters.country },
			{ field: 'region', values: filters.location },
			{ field: 'description', values: [filters.search] },
		]);
	}

	function arraysEqual(a, b) {
		if (a === b) return true;
		if (!Array.isArray(a) || !Array.isArray(b)) return false;
		if (a.length !== b.length) return false;
		const setB = new Set(b);
		return a.every((v) => setB.has(v));
	}

	function openPopover(dimKey, anchor) {
		setActiveDim(dimKey);
		setAnchorEl(anchor);
	}

	function closePopover() {
		setActiveDim(null);
		setAnchorEl(null);
	}

	function applyDimension(dimKey, nextValues) {
		const dim = dimByKey(dimKey);
		const cleaned = (nextValues || []).filter((v) => typeof v === 'string' && v !== '');
		if (!arraysEqual(dim.selected, cleaned)) {
			dim.setSelected(cleaned);
			persistAndEmit(dimKey, cleaned, undefined);
		}
		closePopover();
	}

	function applySearch(nextSearch) {
		const value = typeof nextSearch === 'string' ? nextSearch : '';
		if (value === lastEmittedSearchRef.current) return;
		lastEmittedSearchRef.current = value;
		persistAndEmit(null, null, value);
	}

	function resetFilters() {
		setSelectedMediaType([]);
		setSelectedBrand([]);
		setSelectedMediaSource([]);
		setSelectedCategory([]);
		setSelectedSubCategory([]);
		setSelectedType([]);
		setSelectedSentiment([]);
		setSelectedAuthor([]);
		setSelectedTopic([]);
		setSelectedAdvertType([]);
		setSelectedLanguage([]);
		setSelectedCountry([]);
		setSelectedLocation([]);
		setSearch('');
		setFacets(null);
		lastEmittedSearchRef.current = '';

		const filters = {
			mediaType: [], brand: [], mediaSource: [], category: [], subCategory: [],
			type: [], sentiment: [], author: [], topic: [], advertType: [],
			language: [], country: [], location: [], exclude: [], search: '',
		};
		sessionStorage.setItem('filters', JSON.stringify(filters));

		emitter.emit('getData');
		emitter.emit('resetAll');
	}

	const activeDimension = activeDim ? dimByKey(activeDim) : null;

	return (
		<Box className={classes.container}>
			<Box className={classes.toolbar}>
				<Box className={classes.searchWrap}>
					<TextField
						size="small"
						placeholder="Search keywords"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onBlur={() => applySearch(search)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								applySearch(search);
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon sx={{ fontSize: 18, color: '#999' }} />
								</InputAdornment>
							),
							endAdornment: search ? (
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => { setSearch(''); applySearch(''); }}
										aria-label="clear search"
									>
										<CloseIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</InputAdornment>
							) : null,
							sx: { fontFamily: 'Poppins', fontSize: 13, height: 32, borderRadius: '18px' },
						}}
						sx={{ width: 220 }}
					/>
				</Box>

				<Box className={classes.buttonsWrap}>
					{DIMENSIONS.map((dim) => (
						<FilterButton
							key={dim.key}
							label={dim.label}
							count={dim.selected.length}
							onClick={(anchor) => openPopover(dim.key, anchor)}
						/>
					))}
				</Box>

				<Button
					size="small"
					onClick={resetFilters}
					startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
					sx={{
						textTransform: 'none',
						fontFamily: 'Poppins',
						fontSize: 13,
						color: '#666',
						minHeight: 32,
						borderRadius: '18px',
						px: 1.5,
						'&:hover': { backgroundColor: '#F7F9FC' },
					}}
				>
					Reset All
				</Button>
			</Box>

			{activeDimension && (
				<SetFilterPopover
					open={Boolean(anchorEl) && activeDim === activeDimension.key}
					anchorEl={anchorEl}
					title={activeDimension.label}
					options={activeDimension.options}
					selected={activeDimension.selected}
					onApply={(next) => applyDimension(activeDimension.key, next)}
				/>
			)}
		</Box>
	);
}

const useStyles = makeStyles(() => ({
	container: {
		display: 'flex',
		backgroundColor: '#fff',
		padding: '12px 20px',
		borderBottom: '1px solid #EEE',
	},
	toolbar: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		width: '100%',
	},
	searchWrap: {
		display: 'flex',
		alignItems: 'center',
		marginRight: 4,
	},
	buttonsWrap: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
		flex: 1,
	},
}));
